// Shared enterprise queue table used by both the per-portal and unified Service
// Desk queues. Adds: multi-select + bulk action bar, inline quick-edit of
// status/priority/assignee, assignee avatars, a sticky Key column, a
// comfortable/compact density, and skeleton loading — while delegating the
// non-editable cell rendering to the shared ColumnCell.
import React, { useMemo, useState, useEffect } from 'react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Checkbox } from '../ui/checkbox';
import { Skeleton } from '../ui/skeleton';
import { CaretDown, UserCircle, CheckCircle, X } from '@phosphor-icons/react';
import {
  StatusBadge, PriorityBadge, Dot, STATUS_DOT, PRIORITY_DOT,
  STATUS_OPTIONS, PRIORITY_OPTIONS, statusLabel, priorityLabel,
} from './ticketMeta';
import { ColumnCell, SortHeader, ALL_QUEUE_COLUMNS, QUEUE_COLUMN_LABELS } from './queueColumns';

const initials = (s) => (s ? s.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase() : '?');

export function Avatar({ user, size = 22 }) {
  const s = { width: size, height: size };
  if (user?.avatar) return <img src={user.avatar} alt="" style={s} className="rounded-full object-cover ring-1 ring-zinc-200 shrink-0" />;
  return (
    <span style={s} className="rounded-full bg-gradient-to-br from-zinc-200 to-zinc-300 text-zinc-600 text-[9px] font-bold flex items-center justify-center shrink-0">
      {initials(user?.name || user?.email)}
    </span>
  );
}

const stop = (e) => e.stopPropagation();

function InlineStatus({ value, onChange }) {
  return (
    <span onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="sd-inline-status" className="rounded-full outline-none focus:ring-2 ring-blue-300 hover:opacity-80 transition inline-flex items-center gap-0.5">
            <StatusBadge status={value} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[150px]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400">Set status</DropdownMenuLabel>
          {STATUS_OPTIONS.map(s => (
            <DropdownMenuItem key={s} onSelect={() => s !== value && onChange(s)} className="text-xs">
              <Dot className={`mr-2 ${STATUS_DOT[s]}`} />{statusLabel(s)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

function InlinePriority({ value, onChange }) {
  return (
    <span onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="sd-inline-priority" className="rounded-full outline-none focus:ring-2 ring-blue-300 hover:opacity-80 transition">
            <PriorityBadge priority={value} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[150px]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400">Set priority</DropdownMenuLabel>
          {PRIORITY_OPTIONS.map(p => (
            <DropdownMenuItem key={p} onSelect={() => p !== value && onChange(p)} className="text-xs">
              <Dot className={`mr-2 ${PRIORITY_DOT[p]}`} />{priorityLabel(p)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

function InlineAssignee({ value, members, memberById, onChange }) {
  const cur = memberById?.[value];
  return (
    <span onClick={stop}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="sd-inline-assignee" className="inline-flex items-center gap-1.5 rounded-full px-1 -mx-1 py-0.5 hover:bg-zinc-100 outline-none focus:ring-2 ring-blue-300 transition max-w-[160px]">
            {cur ? <Avatar user={cur} /> : <UserCircle size={22} className="text-zinc-300 shrink-0" />}
            <span className="text-xs text-zinc-600 truncate">{cur?.name || 'Unassigned'}</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px] max-h-[280px] overflow-auto">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400">Assign to</DropdownMenuLabel>
          <DropdownMenuItem onSelect={() => value && onChange(null)} className="text-xs text-zinc-500">
            <UserCircle size={18} className="mr-2 text-zinc-300" /> Unassigned
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {members.map(m => (
            <DropdownMenuItem key={m.user_id} onSelect={() => m.user_id !== value && onChange(m.user_id)} className="text-xs">
              <span className="mr-2"><Avatar user={m} size={18} /></span>{m.name || m.email}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  );
}

// A little labelled dropdown for the bulk action bar.
function BulkMenu({ label, testid, children }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid} className="inline-flex items-center gap-1 text-[13px] font-medium px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition">
          {label} <CaretDown size={12} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[190px] max-h-[300px] overflow-auto">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function QueueTable({
  tickets, columns, ctx = {}, sortBy, sortDir, onSort, onRowClick,
  members = [], memberById = {}, canEdit = false, onQuickEdit, onBulk, density = 'comfortable', loading = false,
}) {
  const [selected, setSelected] = useState(() => new Set());

  // Prune selection to the currently-rendered rows whenever the list changes.
  useEffect(() => {
    setSelected(prev => {
      if (prev.size === 0) return prev;
      const ids = new Set(tickets.map(t => t.ticket_id));
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [tickets]);

  const cellPad = density === 'compact' ? 'py-1.5 px-3' : 'py-3 px-4';
  const colMeta = (c) => ALL_QUEUE_COLUMNS.find(x => x.key === c) || { key: c, label: c === 'portal' ? 'Portal' : (QUEUE_COLUMN_LABELS[c] || c), sortable: true };
  const allSelected = tickets.length > 0 && selected.size === tickets.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(tickets.map(t => t.ticket_id)));
  const toggleOne = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const runBulk = async (action, value) => {
    const ids = [...selected];
    if (!ids.length) return;
    await onBulk?.(action, value, ids);
    clearSel();
  };

  const keyLeft = canEdit ? 44 : 0;

  if (loading) {
    return (
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden" data-testid="sd-queue-skeleton">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-zinc-100 last:border-0">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden" data-testid="sd-queue-table-wrap">
      {canEdit && selected.size > 0 && (
        <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm" data-testid="sd-bulk-bar">
          <CheckCircle size={16} weight="fill" />
          <span className="font-semibold" data-testid="sd-bulk-count">{selected.size} selected</span>
          <div className="h-4 w-px bg-white/30 mx-1" />
          <BulkMenu label="Assign" testid="sd-bulk-assign">
            <DropdownMenuItem onSelect={() => runBulk('assignee', null)} className="text-xs text-zinc-500">
              <UserCircle size={16} className="mr-2 text-zinc-300" /> Unassigned
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {members.map(m => (
              <DropdownMenuItem key={m.user_id} onSelect={() => runBulk('assignee', m.user_id)} className="text-xs">
                <span className="mr-2"><Avatar user={m} size={18} /></span>{m.name || m.email}
              </DropdownMenuItem>
            ))}
          </BulkMenu>
          <BulkMenu label="Status" testid="sd-bulk-status">
            {STATUS_OPTIONS.map(s => (
              <DropdownMenuItem key={s} onSelect={() => runBulk('status', s)} className="text-xs">
                <Dot className={`mr-2 ${STATUS_DOT[s]}`} />{statusLabel(s)}
              </DropdownMenuItem>
            ))}
          </BulkMenu>
          <BulkMenu label="Priority" testid="sd-bulk-priority">
            {PRIORITY_OPTIONS.map(p => (
              <DropdownMenuItem key={p} onSelect={() => runBulk('priority', p)} className="text-xs">
                <Dot className={`mr-2 ${PRIORITY_DOT[p]}`} />{priorityLabel(p)}
              </DropdownMenuItem>
            ))}
          </BulkMenu>
          <button onClick={() => runBulk('status', 'resolved')} data-testid="sd-bulk-resolve"
            className="inline-flex items-center gap-1 text-[13px] font-medium px-2.5 py-1 rounded-md bg-white/15 hover:bg-white/25 transition">
            <CheckCircle size={13} weight="fill" /> Resolve
          </button>
          <button onClick={clearSel} data-testid="sd-bulk-clear" className="ml-auto inline-flex items-center gap-1 text-[13px] px-2 py-1 rounded-md hover:bg-white/15 transition">
            <X size={13} weight="bold" /> Clear
          </button>
        </div>
      )}
      <div className="bg-white overflow-auto max-h-[calc(100vh-320px)]">
        <table className="w-full text-sm" data-testid="sd-queue-table">
          <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 sticky top-0 z-20">
            <tr>
              {canEdit && (
                <th className="sticky left-0 z-30 bg-zinc-50 w-11 px-3 py-2.5">
                  <Checkbox checked={allSelected ? true : (someSelected ? 'indeterminate' : false)} onCheckedChange={toggleAll} data-testid="sd-select-all" />
                </th>
              )}
              {columns.map(c => {
                const m = colMeta(c);
                const sticky = c === 'key';
                return (
                  <SortHeader key={c} col={c} label={m.label} sortable={m.sortable !== false}
                    sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                    className={sticky ? 'sticky z-30 bg-zinc-50' : ''}
                    style={sticky ? { left: keyLeft } : undefined} />
                );
              })}
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => {
              const isSel = selected.has(t.ticket_id);
              const rowBg = isSel ? 'bg-blue-50' : 'hover:bg-blue-50/40';
              const stickyBg = isSel ? 'bg-blue-50' : 'bg-white group-hover:bg-blue-50/40';
              return (
                <tr key={t.ticket_id}
                  onClick={() => onRowClick?.(t)}
                  className={`group border-b border-zinc-100 last:border-0 cursor-pointer transition-colors ${rowBg}`}
                  data-testid={`sd-ticket-row-${t.ticket_id}`}>
                  {canEdit && (
                    <td onClick={stop} style={{ left: 0 }} className={`sticky z-10 ${stickyBg} w-11 px-3 ${cellPad}`}>
                      <Checkbox checked={isSel} onCheckedChange={() => toggleOne(t.ticket_id)} data-testid={`sd-select-${t.ticket_id}`} />
                    </td>
                  )}
                  {columns.map(c => {
                    const sticky = c === 'key';
                    const editable = canEdit && ['status', 'priority', 'assignee'].includes(c);
                    let content;
                    if (editable && c === 'status') content = <InlineStatus value={t.status} onChange={(v) => onQuickEdit(t.ticket_id, 'status', v)} />;
                    else if (editable && c === 'priority') content = <InlinePriority value={t.priority} onChange={(v) => onQuickEdit(t.ticket_id, 'priority', v)} />;
                    else if (editable && c === 'assignee') content = <InlineAssignee value={t.assignee_user_id} members={members} memberById={memberById} onChange={(v) => onQuickEdit(t.ticket_id, 'assignee', v)} />;
                    else content = <ColumnCell col={c} t={t} ctx={ctx} />;
                    return (
                      <td key={c} style={sticky ? { left: keyLeft } : undefined}
                        className={`${cellPad} whitespace-nowrap ${sticky ? `sticky z-10 ${stickyBg}` : ''}`}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
